export type TipoSistema = 'crud-completo' | 'crud-relatorio' | 'integracao-externa' | 'so-consulta';

export type StackFrontend = 'react' | 'angular';

export type EscopoProjeto = 'api-only' | 'api-bff' | 'api-bff-react' | 'api-bff-angular';

export type OperacaoEntidade = 'create' | 'read' | 'update' | 'delete' | 'list';

export interface EntidadeProjetoSpec {
    nome: string;
    operacoes: OperacaoEntidade[];
    campos?: string[];
}

export interface ProjetoSpec {
    modulo: string;
    descricao: string;
    frontend: {
        incluir: boolean;
        stack?: StackFrontend;
        incluirBff: boolean;
    };
    escopo: EscopoProjeto;
    tipoSistema: TipoSistema;
    /** Porta HTTPS do microservice; a HTTP derivada é porta - 2000. */
    porta: number;
    entidades: EntidadeProjetoSpec[];
    opcoes: {
        incluirTestes: boolean;
        incluirFiltroPost: boolean;
    };
}

export interface ProjetoGeradoArquivo {
    path: string;
    conteudo: string;
}

export type PosicaoInsercao = 'Antes' | 'Depois';

/** Edição que o APLICAR.ps1 fará em um arquivo já existente do monorepo. */
export interface MutacaoArquivo {
    path: string;
    descricao: string;
    conteudoInserir: string;
    posicao: PosicaoInsercao;
}

export interface ProjetoGerado {
    modulo: string;
    escopo: string;
    descricao: string;
    arquivos: ProjetoGeradoArquivo[];
    mutacoes: MutacaoArquivo[];
    scriptAplicar: string;
    readme?: string;
}

export const COMANDO_CRIAR_PROJETO = 'criar-projeto-do-zero';

/** Metadata local — espelha ComandoRegistry; garante menu mesmo se API ainda não foi redeployada. */
export const COMANDO_CRIAR_PROJETO_MENU = {
    comando: COMANDO_CRIAR_PROJETO,
    rotulo: 'Projeto do zero',
    descricao: 'Gera estrutura COTIN completa em ZIP',
    icone: 'fa-folder-plus',
    niveis: ['*'],
    exemploPrompt: 'Criar módulo de gestão de contratos com API, BFF e React',
} as const satisfies import('./modelos').ComandoMenu;

export const MIN_PALAVRAS_DESCRICAO = 15;

export const PORTA_MINIMA = 1024;
export const PORTA_MAXIMA = 65535;
export const PORTA_PADRAO = 7101;

/** Campos usados quando a entidade não declara nenhum — ver ProjetoScaffoldContext.CamposEntidade. */
export const CAMPOS_PADRAO_ENTIDADE = ['Nome', 'Descricao', 'Ativo'] as const;

export const TIPOS_SISTEMA: { id: TipoSistema; rotulo: string; descricao: string }[] = [
    { id: 'crud-completo', rotulo: 'CRUD completo', descricao: 'Cadastro, listagem, edição e exclusão' },
    { id: 'crud-relatorio', rotulo: 'CRUD + relatório', descricao: 'CRUD com exportação/relatório' },
    { id: 'integracao-externa', rotulo: 'Integração externa', descricao: 'Consome API ou serviço de terceiros' },
    { id: 'so-consulta', rotulo: 'Só consulta', descricao: 'Leitura e listagem, sem alteração' },
];

export const OPERACOES_ENTIDADE: { id: OperacaoEntidade; rotulo: string }[] = [
    { id: 'create', rotulo: 'Criar' },
    { id: 'read', rotulo: 'Ler' },
    { id: 'update', rotulo: 'Atualizar' },
    { id: 'delete', rotulo: 'Excluir' },
    { id: 'list', rotulo: 'Listagem (filtro POST)' },
];

export function contarPalavras(texto: string): number {
    return texto.trim().split(/\s+/).filter(Boolean).length;
}

export function ehPascalCase(valor: string): boolean {
    return /^[A-Z][a-zA-Z0-9]{2,39}$/.test(valor.trim());
}

/** Remove acentos e caracteres inválidos; garante PascalCase (namespace C#). */
export function normalizarNomeModulo(valor: string): string {
    const partes = valor
        .trim()
        .split(/\s+/)
        .map((parte) =>
            parte
                .normalize('NFD')
                .replace(/\p{M}/gu, '')
                .replace(/[^a-zA-Z0-9]/g, ''),
        )
        .filter(Boolean);

    if (partes.length === 0) return '';

    return partes
        .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
        .join('');
}

export function resolverEscopo(spec: Pick<ProjetoSpec, 'frontend'>): EscopoProjeto {
    if (!spec.frontend.incluir) {
        return spec.frontend.incluirBff ? 'api-bff' : 'api-only';
    }
    // Angular ainda não é gerado — cai em api-bff para não prometer frontend que não sai.
    if (spec.frontend.stack !== 'react') {
        return 'api-bff';
    }
    return 'api-bff-react';
}

export function montarPromptProjeto(spec: ProjetoSpec): string {
    const entidades = spec.entidades
        .map((e) => {
            const ops = e.operacoes.join(', ');
            const campos = e.campos?.length ? ` (campos: ${e.campos.join(', ')})` : '';
            return `${e.nome} [${ops}]${campos}`;
        })
        .join('; ');

    const stack = spec.frontend.incluir
        ? `${spec.frontend.stack?.toUpperCase()}${spec.frontend.incluirBff ? ' + BFF' : ''}`
        : spec.frontend.incluirBff
          ? 'API + BFF'
          : 'somente API';

    return (
        `Criar projeto COTIN do zero: módulo ${spec.modulo}, escopo ${spec.escopo}, stack ${stack}, ` +
        `tipo ${spec.tipoSistema}. Entidades: ${entidades}. ${spec.descricao}`
    );
}

export interface ValidacaoProjetoSpec {
    valido: boolean;
    erros: string[];
    palavrasDescricao: number;
}

export function validarProjetoSpec(spec: ProjetoSpec): ValidacaoProjetoSpec {
    const erros: string[] = [];
    const palavrasDescricao = contarPalavras(spec.descricao);

    if (!ehPascalCase(spec.modulo)) {
        erros.push('Nome do módulo em PascalCase (3 a 40 caracteres, sem acentos).');
    }

    if (palavrasDescricao < MIN_PALAVRAS_DESCRICAO) {
        erros.push(`Descrição com no mínimo ${MIN_PALAVRAS_DESCRICAO} palavras (atual: ${palavrasDescricao}).`);
    }

    if (spec.frontend.incluir) {
        if (spec.frontend.stack === 'angular') {
            erros.push('Geração de frontend Angular ainda não está disponível — selecione React.');
        } else if (spec.frontend.stack !== 'react') {
            erros.push('Selecione a stack do frontend: React.');
        }
    }

    if (!Number.isInteger(spec.porta) || spec.porta < PORTA_MINIMA || spec.porta > PORTA_MAXIMA) {
        erros.push(`Porta do microservice deve estar entre ${PORTA_MINIMA} e ${PORTA_MAXIMA}.`);
    }

    if (spec.entidades.length === 0) {
        erros.push('Informe ao menos uma entidade.');
    }

    const nomes = new Set<string>();
    spec.entidades.forEach((entidade, indice) => {
        if (!ehPascalCase(entidade.nome)) {
            erros.push(`Entidade ${indice + 1}: nome inválido.`);
        } else if (nomes.has(entidade.nome.toLowerCase())) {
            erros.push(`Entidade duplicada: ${entidade.nome}.`);
        } else {
            nomes.add(entidade.nome.toLowerCase());
        }

        if (entidade.operacoes.length === 0) {
            erros.push(`Entidade ${entidade.nome}: selecione ao menos uma operação.`);
        }
    });

    return { valido: erros.length === 0, erros, palavrasDescricao };
}

export function criarSpecInicial(): ProjetoSpec {
    return {
        modulo: '',
        descricao: '',
        frontend: { incluir: true, stack: 'react', incluirBff: true },
        escopo: 'api-bff-react',
        tipoSistema: 'crud-completo',
        porta: PORTA_PADRAO,
        entidades: [{ nome: '', operacoes: ['create', 'read', 'update', 'delete', 'list'] }],
        opcoes: { incluirTestes: true, incluirFiltroPost: true },
    };
}

export function parseProjetoGerado(codigo: string): ProjetoGerado | null {
    const bruto = codigo.trim().replace(/^\uFEFF/, '');
    if (!bruto) return null;

    const direto = tentarParseProjetoJson(bruto);
    if (direto) return direto;

    return extrairProjetoGeradoLeniente(bruto);
}

function tentarParseProjetoJson(bruto: string): ProjetoGerado | null {
    try {
        const parsed = JSON.parse(bruto) as Partial<ProjetoGerado>;
        return normalizarProjetoGerado(parsed);
    } catch {
        return null;
    }
}

function normalizarProjetoGerado(parsed: Partial<ProjetoGerado>): ProjetoGerado | null {
    const modulo = typeof parsed.modulo === 'string' ? parsed.modulo.trim() : '';
    const escopo = typeof parsed.escopo === 'string' ? parsed.escopo.trim() : '';
    const descricao = typeof parsed.descricao === 'string' ? parsed.descricao.trim() : '';
    const arquivos = Array.isArray(parsed.arquivos)
        ? parsed.arquivos
              .filter(
                  (item): item is ProjetoGeradoArquivo =>
                      typeof item === 'object' &&
                      item !== null &&
                      typeof item.path === 'string' &&
                      typeof item.conteudo === 'string' &&
                      item.path.trim().length > 0
              )
              .map((item) => ({ path: item.path.trim(), conteudo: item.conteudo }))
        : [];

    const mutacoes = Array.isArray(parsed.mutacoes)
        ? parsed.mutacoes
              .filter(
                  (item): item is MutacaoArquivo =>
                      typeof item === 'object' &&
                      item !== null &&
                      typeof item.path === 'string' &&
                      typeof item.conteudoInserir === 'string' &&
                      item.path.trim().length > 0
              )
              .map((item) => ({
                  path: item.path.trim(),
                  descricao: typeof item.descricao === 'string' ? item.descricao : '',
                  conteudoInserir: item.conteudoInserir,
                  posicao: item.posicao === 'Antes' ? ('Antes' as const) : ('Depois' as const),
              }))
        : [];

    if (!modulo || arquivos.length === 0) {
        return null;
    }

    return {
        modulo,
        escopo: escopo || 'api-only',
        descricao,
        arquivos,
        mutacoes,
        scriptAplicar: typeof parsed.scriptAplicar === 'string' ? parsed.scriptAplicar : '',
        readme: typeof parsed.readme === 'string' ? parsed.readme : undefined,
    };
}

/** Fallback quando a IA emite JSON inválido (quebras de linha literais dentro de "conteudo"). */
function extrairProjetoGeradoLeniente(bruto: string): ProjetoGerado | null {
    const modulo = extrairCampoJsonString(bruto, 'modulo');
    if (!modulo) return null;

    const arquivos = extrairArquivosJsonLeniente(bruto);
    if (arquivos.length === 0) return null;

    return {
        modulo,
        escopo: extrairCampoJsonString(bruto, 'escopo') ?? 'api-only',
        descricao: extrairCampoJsonString(bruto, 'descricao') ?? '',
        arquivos,
        // Fallback leniente não recupera mutações; o card avisa e o dev aplica pelo README.
        mutacoes: [],
        scriptAplicar: extrairCampoJsonString(bruto, 'scriptAplicar') ?? '',
        readme: extrairCampoJsonString(bruto, 'readme') ?? undefined,
    };
}

function extrairCampoJsonString(bruto: string, chave: string): string | null {
    const marcador = `"${chave}"`;
    const indice = bruto.indexOf(marcador);
    if (indice === -1) return null;
    const valor = lerValorStringJson(bruto, indice + marcador.length);
    return valor?.valor.trim() ? valor.valor.trim() : null;
}

function extrairArquivosJsonLeniente(bruto: string): ProjetoGeradoArquivo[] {
    const arquivos: ProjetoGeradoArquivo[] = [];
    let cursor = 0;

    while (cursor < bruto.length) {
        const indicePath = bruto.indexOf('"path"', cursor);
        if (indicePath === -1) break;

        const pathLido = lerValorStringJson(bruto, indicePath + 6);
        if (!pathLido?.valor.trim()) {
            cursor = indicePath + 6;
            continue;
        }

        const indiceConteudo = bruto.indexOf('"conteudo"', pathLido.fim);
        if (indiceConteudo === -1) break;

        const conteudoLido = lerValorStringJson(bruto, indiceConteudo + 10);
        if (!conteudoLido) {
            cursor = indiceConteudo + 10;
            continue;
        }

        arquivos.push({ path: pathLido.valor.trim(), conteudo: conteudoLido.valor });
        cursor = conteudoLido.fim;
    }

    return arquivos;
}

function lerValorStringJson(
    texto: string,
    posInicial: number
): { valor: string; fim: number } | null {
    let i = posInicial;
    while (i < texto.length && texto[i] !== ':') i++;
    if (i >= texto.length) return null;
    i++;
    while (i < texto.length && /\s/.test(texto[i])) i++;
    if (texto[i] !== '"') return null;
    i++;

    let valor = '';
    while (i < texto.length) {
        const ch = texto[i];
        if (ch === '\\' && i + 1 < texto.length) {
            const prox = texto[i + 1];
            const mapa: Record<string, string> = {
                n: '\n',
                r: '\r',
                t: '\t',
                '"': '"',
                '\\': '\\',
            };
            valor += mapa[prox] ?? prox;
            i += 2;
            continue;
        }
        if (ch === '"') {
            let j = i + 1;
            while (j < texto.length && /\s/.test(texto[j])) j++;
            if (j >= texto.length || ',}]'.includes(texto[j])) {
                return { valor, fim: j };
            }
            valor += ch;
            i++;
            continue;
        }
        valor += ch;
        i++;
    }

    return valor.length > 0 ? { valor, fim: i } : null;
}
